import styled from "@emotion/styled";
import {observer} from "mobx-react";
import React from "react";

import {STRING_TYPE} from "../../../stores/database-template/database-form-base";
import {useAuthenticationStore} from "../../../stores/store-hooks";
import {
    AddButton,
    EditButton,
    EditIconButton,
    TabButton,
    TrashIconButton,
} from "../../atoms/buttons/buttons";
import {
    ActionsColumn,
    DisplayFlexSpaceBetween,
    PageContainer,
} from "../../atoms/layouts/category-layout-containers";
import {Td, Tr} from "../../atoms/tables/table";
import ImageLink from "../../molecules/images/image-link";
import {AdminBreadcrumb} from "../../organisms/admin-breadcrumb/admin-breadcrumb";
import {DeleteDialog} from "../../organisms/delete-dialog/delete-dialog";
import {TableLayout} from "../../organisms/table-layout/table-layout";
import {AppLayout} from "../../templates/app-layout/app-layout";
import {DatabaseTemplateDetailPageStore} from "./database-template-detail-store";

const TemplateDescriptionSection = styled.section`
    margin-bottom: 16px;
`;

const TemplateDescriptionLabel = styled.p`
    margin: 0 0 4px;
    font-size: 12px;
    color: #666;
`;

const TemplateDescriptionText = styled.p`
    margin: 0;
    white-space: pre-wrap;
    line-height: 1.6;
`;

const ImageCell: React.FC<{src?: string; alt?: string; width?: number; height?: number}> = ({
    src,
    alt = "",
    width = 140,
}) => {
    return (
        <div
            style={{
                width,
                minWidth: width,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
            }}
        >
            {src ? <ImageLink src={src} slug={alt} alt={alt} /> : <div style={{width}} />}
        </div>
    );
};

const DatabaseTemplateDetailPage: React.FC<{pageStore: DatabaseTemplateDetailPageStore}> = observer(
    (props) => {
        const pageStore = props.pageStore;
        const authenticationStore = useAuthenticationStore();
        const canDelete =
            authenticationStore.hasManageAdminUsersPermission ||
            authenticationStore.hasManageUsersPermission;
        const managementRows = pageStore.templateItemList.itemList.map((data, index) => {
            // その行の items を columnId -> item のマップに
            const itemMap = new Map(data.items.map((it) => [it.columnId, it]));
            return (
                <Tr key={data.itemId}>
                    <Td>{index + 1}</Td>
                    <Td>{data.itemLabel}</Td>

                    {/* テンプレートの列順に必ずセルを出す */}
                    {pageStore.templateColumnList.map((col) => {
                        const it = itemMap.get(col.id);
                        const isImage = col.type !== STRING_TYPE;

                        if (isImage) {
                            return (
                                <Td key={col.id}>
                                    <ImageCell
                                        src={it?.value || ""}
                                        alt={col.label}
                                        width={140}
                                        height={90}
                                    />
                                </Td>
                            );
                        }

                        return (
                            <Td style={{whiteSpace: "pre-wrap"}} key={col.id}>
                                {it?.value ?? "" /* STRING は空文字で保持 */}
                            </Td>
                        );
                    })}

                    <Td>
                        <EditIconButton
                            onClick={() => pageStore.toDataRecordEditPage(data.itemId)}
                        />
                        {canDelete && (
                            <TrashIconButton
                                onClick={() =>
                                    pageStore.confirmDeleteTemplate(data.itemId, data.itemLabel)
                                }
                            />
                        )}
                    </Td>
                </Tr>
            );
        });

        return (
            <AppLayout
                breadcrumb={
                    <AdminBreadcrumb
                        factory={(builder) =>
                            builder.databaseTemplates().custom(pageStore.templateName).build()
                        }
                    />
                }
            >
                <PageContainer>
                    <TemplateDescriptionSection aria-labelledby="template-description-label">
                        <TemplateDescriptionLabel id="template-description-label">
                            テンプレートの説明
                        </TemplateDescriptionLabel>
                        <TemplateDescriptionText>
                            {pageStore.templateNote || "説明はありません。"}
                        </TemplateDescriptionText>
                    </TemplateDescriptionSection>
                    <DisplayFlexSpaceBetween>
                        <ActionsColumn aria-label="テンプレートの操作">
                            <TabButton
                                label="テンプレートの定義"
                                isActive={pageStore.selectedTab === "template"}
                                onClick={() => pageStore.setTab("template")}
                            />
                            <TabButton
                                label="データの管理"
                                isActive={pageStore.selectedTab === "management"}
                                onClick={() => pageStore.setTab("management")}
                            />
                        </ActionsColumn>
                        <ActionsColumn aria-label="テンプレートのアクション">
                            {pageStore.selectedTab === "management" && (
                                <AddButton onClick={() => pageStore.toDataRecordCreatePage()}>
                                    データの追加
                                </AddButton>
                            )}
                            {pageStore.selectedTab === "template" && (
                                <EditButton onClick={() => pageStore.toEditPage()}>
                                    定義の編集
                                </EditButton>
                            )}
                        </ActionsColumn>
                    </DisplayFlexSpaceBetween>
                    <TableLayout areaLabel="テンプレート詳細" columns={pageStore.columns}>
                        {pageStore.selectedTab === "template" &&
                            pageStore.templateColumnList.map((row) => (
                                <Tr key={row.id}>
                                    <Td>{row.slug}</Td>
                                    <Td>{row.label}</Td>
                                    <Td>{row.type === STRING_TYPE ? "STRING" : "IMAGE"}</Td>
                                    <Td>{row.note}</Td>
                                </Tr>
                            ))}

                        {pageStore.selectedTab === "management" && managementRows}
                    </TableLayout>
                </PageContainer>
                <DeleteDialog />
            </AppLayout>
        );
    },
);

export default DatabaseTemplateDetailPage;
